import { createSelector } from "reselect";

import { useSelector, useDispatch } from "react-redux";
import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
} from "react";
import { download } from "Core/data/Helpers";
import {
  ACTION_ENUM,
  DEVICE_MODEL_ENUM,
  MAX_ATTEMPTS,
  MODE_ENUM,
  ROTATE_ENUM,
  STATUS_ENUM,
  THUMBNAILURL_SMARTWITNESS,
  SIM_PROVIDER_NAME_ENUM,
} from "./constants";
import _ from "lodash";

import { endpoints } from "Core/defaultValues";
import { getVideoFootage, getVideoLastSnapshot, reset } from "./redux/slice";

import { useIntl } from "react-intl";
import { NotificationManager } from "Components/Notifications";


let VIDFLEET_VIDEO_RETRY_TIMER = null;

const footageResponse = (state) => state.videoRedux.footageResponse;
const loadingFootage = (state) => state.videoRedux.loadingFootage;
const footageError = (state) => state.videoRedux.footageError;

const footageServer = (readingId, deviceId) => {
  return createSelector([footageResponse, loadingFootage, footageError], (response, loading, error) => {
    return [loading[`${readingId}-${deviceId}`], response[`${readingId}-${deviceId}`], error[`${readingId}-${deviceId}`]];
  });
};

const snapshotResponse = (state) => state.videoRedux.snapshotResponse;
const loadingSnapshot = (state) => state.videoRedux.loadingSnapshot;
const snapshotError = (state) => state.videoRedux.snapshotError;

const snapshotServer = (deviceId, unitTime) => {
  return createSelector([snapshotResponse, loadingSnapshot, snapshotError], (response, loading, error) => {
    return [loading[`${deviceId}-${unitTime}`], response[`${deviceId}-${unitTime}`], error[`${deviceId}-${unitTime}`]];
  });
};

export const useVideo = (ref, props) => {
  const dispatch = useDispatch();
  const videoPlayerRef = useRef();
  const { messages } = useIntl();
  const {
    readingId,
    deviceId,
    unitTime,
    mode,
    autoplay,
    deviceModel,
    dewrapImage,
    disabled,
    timeZone,
    name,
    disableDownload = false
  } = props;

  const [url, setUrl] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [showLoading, setShowLoading] = useState(null);
  const [message, setMessage] = useState("");
  const [openImg, setOpenImg] = useState(false);

  const [loadingFootage, footageResponse, footageError] = useSelector(
    footageServer(readingId, deviceId),
    (prevProps, nextProps) => {
      return _.isEqual(prevProps, nextProps);
    }
  );

  const [loadingSnapshot, snapshotResponse, snapshotError] = useSelector(
    snapshotServer(deviceId, unitTime),
    (prevProps, nextProps) => {
      return _.isEqual(prevProps, nextProps);
    }
  );

  useImperativeHandle(ref, () => ({
    downloadVideo: downloadVideo,
    downloadPicture: downloadPicture,
    retry: retry
  }));

  useEffect(() => {
    if (mode === MODE_ENUM.playback) {
      if (props.url && props.thumbnailUrl) {
        setMessage("");
        setUrl(url);
        setThumbnail(thumbnailUrl);

        if (autoplay) {
          setPlayingVideo(true);
          setShowPlayButton(false);
        } else {
          setShowPlayButton(true);
        }

        setShowLoading(false);
      } else {
        dispatch(getVideoFootage({ readingId, deviceId }));
      }
    } else if (mode === MODE_ENUM.snapshot) {
      setShowPlayButton(false);
      dispatch(getVideoLastSnapshot({ deviceId, unitTime }));
    }
  }, [
    readingId,
    deviceId,
    unitTime,
    autoplay,
    mode,
  ]);

  useEffect(() => {
    if (props.thumbnailUrl) {
      if (props?.thumbnailUrl?.includes(THUMBNAILURL_SMARTWITNESS)) {
        setThumbnail(`${endpoints.SMARTWITNESS_API_URL}${props?.thumbnailUrl}`);
      } else {
        setThumbnail(props.thumbnailUrl);
      }
    }
  }, [thumbnailUrl, props.thumbnailUrl]);

  useEffect(() => {
    if (footageResponse) {
      setShowPlayButton(false);
      setPlayingVideo(false);
      setShowLoading(true);
      clearTimeout(VIDFLEET_VIDEO_RETRY_TIMER);

      setMessage("");
      setUrl(footageResponse?.url);
      setThumbnailUrl(footageResponse?.thumbnailUrl);

      if (autoplay) {
        setPlayingVideo(true);
        setShowPlayButton(false);
      } else {
        setShowPlayButton(true);
      }

      setShowLoading(false);
    }
  }, [footageResponse, autoplay]);

  useEffect(() => {
    if (footageError) {
      setMessage(messages ? messages[footageError || "video_errorApi"] : "Error");
      setShowPlayButton(true);
      setPlayingVideo(false);
      setShowLoading(false);

      dispatch(reset({ footageError: {} }));
    }
  }, [footageError]);

  useEffect(() => {
    if (snapshotResponse) {
      setShowPlayButton(false);
      setPlayingVideo(false);

      setMessage("");
      setUrl(snapshotResponse?.url);
      setThumbnailUrl(snapshotResponse?.thumbnailUrl);
    }
  }, [snapshotResponse]);

  useEffect(() => {
    if (snapshotError) {
      setMessage(messages ? messages[snapshotError || "video_snapshotErrorApi"] : "Error");
      setShowPlayButton(true);
      setPlayingVideo(false);
      setShowLoading(false);

      dispatch(reset({ snapshotError: {} }));
    }
  }, [snapshotError]);

  let isUpsidedown = false;
  if (
    footageResponse?.rotate === ROTATE_ENUM.upsidedown
  ) {
    isUpsidedown = true;
  }

  let options = {
    autoplay: autoplay,
    controls: true,
    url: url,
    is360: deviceModel === DEVICE_MODEL_ENUM.device360 ? true : false,
    downloadable: disableDownload
      ? false
      : mode === MODE_ENUM.streaming
        ? false
        : true,
    mode: mode,
    notice360Message: messages ? messages["vidFleet_360Message"] : "Error",
    errorMessage: messages ? messages["vidFleet_retryMessage"] : "Error",
    timeoutMessage: messages ? messages["vidFleet_timeout"] : "Error",
    finishedMessage: messages ? messages["vidFleet_stoppedCamera"] : "Error",
    id: `${readingId}-${deviceId}`,
    bigPlayButton: false,
    name: name || `${readingId}-${deviceId}`,
  };

  //functions
  const downloadVideo = useCallback(() => {
    if (url) {
      setLoading(true);
      download(url, `${name || `${readingId}-${deviceId}`}.mp4`);
      NotificationManager.success(
        messages['exportSuccess'],
        messages['success'],
        4000,
        null,
        null,
        'success',
        null
      );
    }
  });

  const downloadPicture = async () => {
    if (thumbnailUrl) {
      try {
        await fetch(thumbnailUrl)
          .then((resp) => resp.blob())
          .then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = `${name || id}.jpg`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
          });
        NotificationManager.success(
          messages['exportSuccess'],
          messages['success'],
          4000,
          null,
          null,
          'success',
          null
        );
      } catch (e) {
        return window.open(thumbnailUrl, "_blank", "noopener,noreferrer");
      }
    }
  };

  const retry = () => {
    setMessage("");
    if (mode === MODE_ENUM.playback) {
      dispatch(getVideoFootage({ readingId, deviceId }));
    } else if (mode === MODE_ENUM.snapshot) {
      dispatch(getVideoLastSnapshot({ deviceId, unitTime }));
    }
  }

  return {
    readingId,
    deviceId,
    unitTime,
    disabled,
    deviceModel,
    dewrapImage,
    id: `${readingId}-${deviceId}`,
    isUpsidedown,
    videoPlayerRef,
    options,
    footageResponse,
    snapshotResponse,
    footageError,
    snapshotError,
    timeZone,
    thumbnailUrl,
    retry,
    setPlayingVideo,
    setShowPlayButton,
    setShowLoading,
    setOpenImg,
    playingVideo,
    showPlayButton,
    showLoading,
    message,
    openImg,
    loadingFootage,
    loadingSnapshot,
    error: footageError || snapshotError,
    loading: loadingFootage || loadingSnapshot || showLoading,
    mode,
    url,
  };
};
